import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { compose } from 'ramda';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

import { normalizeJssFields } from '_utils/helpers/jss';
import { useDataLayer } from '_utils/hooks';
import { handleLinkClickEvent } from '_utils/helpers/trackEvents';
import {
  Container,
  LargeImageContainer,
  GridContainer,
  Row,
  Column,
  SmallImageContainer,
  DetailsContainer,
  Category,
  Title,
  TitleBanner,
  LinkIconContainer,
  LinkIconBannerContainer,
  ArrowWrapper,
  ArrowBannerWrapper,
  Description,
  AttributesWrapper,
  ContainedImage,
  CoverImage,
  SecondaryContainer,
  SecondaryRow,
  SecondaryItem,
  Circle,
  TitleIconWrapper,
  HeaderText,
  HeaderContainer
} from './StyledFeaturedContent';

import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { getContentType } from '_utils/helpers/contentType';
import GridVisualiser from '_utils/components/Corporate/Grid/GridVisualiser';
import ContentTypeAttributes from '_utils/components/Corporate/ContentTypeAttributes';
import { FeaturedContentConfig } from './definitions';
import SecondaryCard from './SecondaryCard';
import Arrow from '_utils/icons/Arrow';
import External from '_utils/icons/External';
import NavLink from '../NavItem';
import { createSrcSet, convertSrcJss } from '_utils/media';
import { imageTransparentPlaceholderSrc } from '_utils/styles';
import { getLink, isValidLinkObject } from '_utils/helpers/link';
import FeaturedContentIcon from "_utils/components/Corporate/FeaturedContentIcon";
import PropTypes from 'prop-types';

const bgClasses = {
  secondary: 'bg-secondary',
  tertiary: 'bg-tertiary',
  lightPink: 'bg-secondary',
  lightOrange: 'bg-secondary'
};

const imgSizeClasses = {
  small: 'simple-image',
  featured: 'featured-image',
  none: 'no-image'
};

const imgPosClasses = {
  left: 'left-image',
  right: 'right-image'
};

const EditContainer = compose(withEditModeNotice)(styled.div``);

function getImageColumnConfig(config: FeaturedContentConfig) {
  const order = config.imagePosition === 'right' ? 2 : 1;

  if (config.imageSize === 'none') {
    return {};
  }

  if (config.imageSize === 'featured') {
    return {};
  }

  // Simple / Small
  return {
    col: { xs: 8, lg: 5 },
    offset: { xs: 0, lg: 0 },
    order: { xs: 1, lg: order }
  };
}

function getDetailsColumnConfig(config: FeaturedContentConfig) {
  const order = config.imagePosition === 'right' ? 1 : 2;

  if (config.imageSize === 'none') {
    return { col: { xs: 12 } };
  }

  if (config.imageSize === 'featured') {
    const offsetLg = config.imagePosition === 'left' ? 7 : 0;

    return {
      col: { xs: 12, lg: 5 },
      offset: { xs: 0, lg: offsetLg },
      order: { xs: 2, lg: order }
    };
  }

  // default to Simple / Small
  return {
    col: { xs: 12, lg: 6 },
    offset: { xs: 0, lg: config.imagePosition === 'right' ? 0 : 1 },
    order
  };
}

function buildConfig(options): FeaturedContentConfig {
  let config: FeaturedContentConfig = {
    background: 'tertiary',
    imageSize: 'featured',
    imagePosition: 'left'
  };

  if (options?.colour?.toLowerCase() === 'light beige') {
    config.background = 'secondary';
  }

  if (options?.colour?.toLowerCase() === 'light pink') {
    config.background = 'lightPink';
  }

  if (options?.colour?.toLowerCase() === 'light orange') {
    config.background = 'lightOrange';
  }

  if (options?.imageSize?.toLowerCase() === 'featured') {
    config.imageSize = 'featured';
  }

  if (options?.imageSize?.toLowerCase() === 'simple') {
    config.imageSize = 'small';
  }

  if (options?.imageSize?.toLowerCase() === 'none' || !options.image?.value?.src) {
    config.imageSize = 'none';
  }

  if (options?.imagePosition?.toLowerCase() === 'right') {
    config.imagePosition = 'right';
  }

  return config;
}

const isValidSecondaryItem = (item) => {
  if (!item) {
    return false;
  }

  // TODO convey validation to author
  const { title } = item;

  return Boolean(title);
};

function FeaturedContent({ sitecoreContext, rendering, editMode }) {
  const dataLayer = useDataLayer();

  const handleLinkClick = (url: string) => {
    if (!dataLayer) {
      return;
    }

    const pageTitle = sitecoreContext?.route?.fields?.pageTitle?.value || '';
    const pageUrl = window.location.pathname;

    handleLinkClickEvent(dataLayer, url, pageTitle, pageUrl);
  };

  const fields = rendering?.fields?.data?.datasource || rendering?.fields;

  if (!fields) {
    return (
      <EditContainer
        noticeEnabled
        noticeLevel="warning"
        noticeMessage={'Featured content component requires data source to render'}
      />
    );
  }

  const normalized = normalizeJssFields(fields);

  let {
    backgroundColour,
    imagePosition,
    imageSize,
    items,
    categoryTitle,
    categoryIcon
  } = normalized;

  backgroundColour = backgroundColour?.value;
  imagePosition = imagePosition?.value;
  imageSize = imageSize?.value;
  categoryTitle = categoryTitle?.value;
  categoryIcon = categoryIcon?.value;

  const hasItems = items && items.length;

  if (!hasItems) {
    if (editMode) {
      return (
        <EditContainer
          noticeEnabled
          noticeLevel="warning"
          noticeMessage={'Featured content component has no items and will not be displayed'}
        />
      );
    } else {
      return null;
    }
  }

  const primaryItem = items[0];

  let secondaryItems = items?.slice(1, 4) || [];

  if (!editMode) {
    secondaryItems = secondaryItems.filter(isValidSecondaryItem);
  }

  const { image, title, description, attributes, primaryCategory, type, link } = primaryItem;
  const anchorId = title
    ? title
        .toLowerCase()
        .split(' ')
        .join('-')
    : '';

  let imageWidth = image?.value?.width;
  let imageHeight = image?.value?.height;

  const linkObject = getLink(link);

  imageWidth = parseInt(imageWidth) || 0;
  imageHeight = parseInt(imageHeight) || 0;

  let imageAspect: 'portrait' | 'landscape' = imageHeight > imageWidth ? 'portrait' : 'landscape';

  let contentType = getContentType(type);

  const config = buildConfig({
    colour: backgroundColour,
    imageSize,
    imagePosition,
    image
  });

  const bgColorClass = bgClasses[config.background];
  const imgSizeClass = imgSizeClasses[config.imageSize];
  const imgPosClass = imgPosClasses[config.imagePosition];
  const imageConfig = getImageColumnConfig(config);
  const detailsConfig = getDetailsColumnConfig(config);
  const secondaryItemsClass = secondaryItems.length ? 'secondary-items' : '';

  const secondaryItemConfig = {
    col: { xs: 12, lg: 12 / secondaryItems.length }
  };

  let imageProps = {};

  if (config.imageSize !== 'none' && image?.value?.src) {
    imageProps = {
      className: 'lazyload',
      src: imageTransparentPlaceholderSrc,
      'data-src': convertSrcJss(image.value.src, 640),
      alt: image?.value?.alt
    };

    if (config.imageSize === 'small') {
      imageProps['data-srcset'] = createSrcSet(image.value.src, [640, 1200]);
    }

    if (config.imageSize === 'featured') {
      imageProps['data-srcset'] = createSrcSet(image.value.src, [640, 1200, 1600]);
    }
  }

  let containerNotice = {
    noticeEnabled: true,
    noticeLevel: hasItems ? 'info' : 'warning',
    noticeMessage: hasItems
      ? `Please use 'Edit related item' screen to modify items in the Featured content component.`
      : `Featured content component has no items.  Please use 'Edit related item' screen to add items.`
  };

  return (
    <EditContainer {...containerNotice}>
      <Container
        id={anchorId || undefined}
        $background={config.background}
        className={`${bgColorClass} ${imgSizeClass} ${imgPosClass} ${secondaryItemsClass}`}
      >
        {false && <GridVisualiser />}
        {config.imageSize === 'featured' && (
          <LargeImageContainer $background={config.background}>
            <CoverImage {...imageProps} />
          </LargeImageContainer>
        )}
        <GridContainer>
          <Row>
            {config.imageSize === 'small' && (
              <Column $config={imageConfig}>
                <SmallImageContainer className={`${imageAspect}`}>
                  <ContainedImage {...imageProps} />
                </SmallImageContainer>
              </Column>
            )}
            <Column $config={detailsConfig}>
              <Circle $config={config}>
                <img alt="" src={imageTransparentPlaceholderSrc} />
              </Circle>
              <DetailsContainer>
                {(categoryIcon || categoryTitle) && (
                  <HeaderContainer>
                    {categoryIcon && <FeaturedContentIcon icon={categoryIcon} />}
                    {categoryTitle && <HeaderText>{categoryTitle}</HeaderText>}
                  </HeaderContainer>
                )}
                {primaryCategory && <Category>{primaryCategory}</Category>}
                {title && (
                  <>
                    {categoryIcon || categoryTitle ? (
                      <NavLink
                        StyledLink={LinkIconBannerContainer}
                        link={linkObject}
                        onClick={() => handleLinkClick(linkObject?.url)}
                      >
                        <TitleBanner>
                          {title}
                          {isValidLinkObject(linkObject) && (
                            <TitleIconWrapper>
                              &#65279;
                              <ArrowBannerWrapper>
                                <Arrow ariaHidden={true} />
                              </ArrowBannerWrapper>
                            </TitleIconWrapper>
                          )}
                        </TitleBanner>
                      </NavLink>
                    ) : (
                      <NavLink
                        StyledLink={LinkIconContainer}
                        link={linkObject}
                        onClick={() => handleLinkClick(linkObject?.url)}
                      >
                        <Title>
                          {title}
                          {isValidLinkObject(linkObject) && (
                            <TitleIconWrapper>
                              &#65279;
                              <ArrowWrapper>
                                {linkObject.type !== 'external' && <Arrow ariaHidden={true} />}
                                {linkObject.type === 'external' && <External ariaHidden={true} />}
                              </ArrowWrapper>
                            </TitleIconWrapper>
                          )}
                        </Title>
                      </NavLink>
                    )}
                  </>
                )}

                {description && <Description field={{ value: description }} $isBanner={categoryIcon || categoryTitle}/>}
                <ContentTypeAttributes
                  attributes={attributes}
                  contentType={contentType}
                  Wrapper={AttributesWrapper}
                />
              </DetailsContainer>
            </Column>
          </Row>
        </GridContainer>
      </Container>
      {secondaryItems.length > 0 && (
        <SecondaryContainer>
          <SecondaryRow as="ul">
            {secondaryItems.map((item, index) => (
              <SecondaryItem as="li" key={index} $config={secondaryItemConfig}>
                <SecondaryCard {...item} />
              </SecondaryItem>
            ))}
          </SecondaryRow>
        </SecondaryContainer>
      )}
    </EditContainer>
  );
}

FeaturedContent.propTypes = {
  sitecoreContext: PropTypes.shape({
    route: PropTypes.shape({
      fields: PropTypes.shape({
        pageTitle: PropTypes.shape({
          value: PropTypes.string
        })
      })
    })
  }),
  rendering: PropTypes.shape({
    fields: PropTypes.shape({
      data: PropTypes.shape({
        datasource: PropTypes.object
      })
    })
  }),
  editMode: PropTypes.bool
};

export default compose(withSitecoreContext(), withEditMode)(FeaturedContent);

